import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { BSL_STATUS } from "../Sidebar/utils/sidebarStorage";
import ApplyForIbanModal from "../Common/ApplyForIbanModal";
import AccountBalanceCard from "./AccountBalanceCard";
import AccountBalanceCardSkeleton from "./AccountBalanceCardSkeleton";

const ALLOWED_CURRENCIES = ["USD", "LBP"];

function ApplyForIbanCard() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="new-balance-card new-balance-card--apply-iban"
        onClick={() => setOpen(true)}
      >
        <span className="new-balance-card__apply-iban-icon" aria-hidden="true">
          <img src="/Images/bank.svg" alt="" />
        </span>

        <div className="new-balance-card__apply-iban-copy">
          <strong>Link your BSL Account</strong>
          <p>Link your BSL bank account.</p>
        </div>

        <span className="new-balance-card__apply-iban-action" aria-hidden="true">
          <FontAwesomeIcon icon={faPlus} />
        </span>
      </button>

      <ApplyForIbanModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function buildCards(sections = [], bankAccountStatus) {
  const cards = [];

  sections.forEach((section) => {
    const balances = Array.isArray(section?.balances) ? section.balances : [];
    const isBsl = section?.id === "bsl";
    const bslStatus = Number.isFinite(section?.status)
      ? section.status
      : bankAccountStatus;

    if (isBsl && bslStatus === BSL_STATUS.apply) {
      cards.push({
        id: `${section.id}-apply-iban`,
        isApplyIban: true,
      });
      return;
    }

    const currencies = isBsl && balances.length === 0 &&
      [BSL_STATUS.apply, BSL_STATUS.pending].includes(bslStatus)
        ? ALLOWED_CURRENCIES.map((currency) => ({
            currency,
            available: 0,
            book: 0,
            held: 0,
          }))
        : balances;

    currencies.forEach((balance, index) => {
      cards.push({
        id: `${section.id}-${balance.accountNumber || balance.currency || "account"}-${index}`,
        bankId: section.id,
        bankTitle: section.id === "bsl" ? "BSL Account" : "Suyool Account",
        bankSubtitle: section.id === "bsl" ? "Bank balances" : "Wallet balances",
        iconSrc:
          section.id === "bsl"
            ? "https://suyoolportalpublic.blob.core.windows.net/photos/bslImagesDas/BSL-logo.svg"
            : "/Images/suyool-colored-logo-fav.png",
        iconAlt: section.id === "bsl" ? "BSL logo" : "Suyool logo",
        accountNumber: balance.accountNumber,
        currency: balance.currency,
        available: balance.available,
        book: balance.book,
        held: balance.held,
        connectionState: isBsl ? balance.connectionState : undefined,
        lastBalanceDate: isBsl ? balance.lastBalanceDate : undefined,
        status: isBsl ? bslStatus : BSL_STATUS.approved,
      });
    });
  });

  return cards;
}

function buildSectionCards(section, bankAccountStatus) {
  return buildCards(section ? [section] : [], bankAccountStatus);
}

function buildSkeletonSections() {
  return [
    {
      id: "suyool",
      cards: [{ id: "suyool-USD" }, { id: "suyool-LBP" }],
    },
    {
      id: "bsl",
      cards: [{ id: "bsl-USD" }, { id: "bsl-LBP" }],
    },
  ];
}

export default function BalanceCardsGrid({
  sections = [],
  bankAccountStatus,
  loading = false,
  onCardClick,
}) {
  const railRef = useRef(null);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const sectionCards = useMemo(
    () =>
      sections
        .map((section) => ({
          id: section?.id || "section",
          cards: buildSectionCards(section, bankAccountStatus),
        }))
        .filter((section) => section.cards.length > 0),
    [sections, bankAccountStatus],
  );

  console.log("sectionCards", sectionCards);
  const displaySections =
    loading && sectionCards.length === 0 ? buildSkeletonSections() : sectionCards;
  const displayCards = displaySections.flatMap((section) => section.cards);

  console.log("displayCards", displayCards);
  const shouldShowScrollAction = displayCards.length > 4;

  const updateScrollState = useCallback(() => {
    const rail = railRef.current;
    if (!rail) {
      setCanScrollNext(false);
      return;
    }

    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
    const hasNext = maxScrollLeft - rail.scrollLeft > 2;
    setCanScrollNext(hasNext);
    setIsAtEnd(maxScrollLeft > 2 && !hasNext);
  }, []);

  useEffect(() => {
    updateScrollState();

    const rail = railRef.current;
    if (!rail) return undefined;

    rail.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      rail.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [displayCards.length, updateScrollState]);

  const handleScrollNext = () => {
    const rail = railRef.current;
    if (!rail) return;

    if (isAtEnd) {
      rail.scrollTo({
        left: 0,
        behavior: "smooth",
      });
      return;
    }

    rail.scrollBy({
      left: rail.clientWidth,
      behavior: "smooth",
    });
  };

  const handleCardClick = (event, card) => {
    if (!onCardClick || loading) return;

    const target = event.target;
    const interactiveElement = target?.closest?.(
      "button, a, input, select, textarea, [role='button'], [data-stop-card-click]",
    );

    if (interactiveElement && interactiveElement !== event.currentTarget) {
      return;
    }

    onCardClick(card);
  };

  const handleCardKeyDown = (event, card) => {
    if (!onCardClick || loading) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    onCardClick(card);
  };

  return (
    <section
      className={`new-balance-grid ${
        shouldShowScrollAction ? "has-scroll-action" : ""
      }`}
      aria-label="Quick Snapshot"
    >
      <h2 className="new-balance-grid__title">Quick Snapshot</h2>
      <div className="new-balance-grid__viewport">
        <div className="new-balance-grid__rail" ref={railRef}>
          {displayCards.map((card) => {
            const isCardClickable =
              Boolean(onCardClick) && !loading && !card.isApplyIban;

            return (
              <div
                key={card.id}
                className={`new-balance-grid__item ${
                  isCardClickable ? "is-clickable" : ""
                }`}
                onClick={
                  isCardClickable
                    ? (event) => handleCardClick(event, card)
                    : undefined
                }
                onKeyDown={
                  isCardClickable
                    ? (event) => handleCardKeyDown(event, card)
                    : undefined
                }
                role={isCardClickable ? "button" : undefined}
                tabIndex={isCardClickable ? 0 : undefined}
              >
                {loading ? (
                  <AccountBalanceCardSkeleton />
                ) : card.isApplyIban ? (
                  <ApplyForIbanCard />
                ) : (
                  <AccountBalanceCard {...card} loading={loading} />
                )}
              </div>
            );
          })}
        </div>
        {shouldShowScrollAction ? (
          <button
            type="button"
            className={`new-balance-grid__scroll-next ${
              isAtEnd ? "is-return" : ""
            }`}
            onClick={handleScrollNext}
            disabled={!canScrollNext && !isAtEnd}
            aria-label={
              isAtEnd ? "Return to first balance card" : "Show more balance cards"
            }
          >
            <FontAwesomeIcon icon={isAtEnd ? faChevronLeft : faChevronRight} />
          </button>
        ) : null}
      </div>
    </section>
  );
}
